import { useEffect, useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Loader2, Calendar, Pencil, Printer, FileText, StickyNote } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const DAYS = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
const DAY_COLORS = [
  "bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800",
  "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800",
  "bg-purple-50 border-purple-200 dark:bg-purple-950/30 dark:border-purple-800",
  "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800",
  "bg-rose-50 border-rose-200 dark:bg-rose-950/30 dark:border-rose-800",
  "bg-gray-50 border-gray-200 dark:bg-gray-950/30 dark:border-gray-800",
  "bg-cyan-50 border-cyan-200 dark:bg-cyan-950/30 dark:border-cyan-800",
];

interface SlotForm {
  day_of_week: number;
  start_time: string;
  end_time: string;
  subject: string;
  teacher_id: string;
  room: string;
  notes: string;
  assignment_id: string;
}

const emptySlot: SlotForm = { day_of_week: 0, start_time: "08:00", end_time: "08:45", subject: "", teacher_id: "", room: "", notes: "", assignment_id: "" };

interface Props {
  classes: any[];
  staff: any[];
  emptyStateMessage?: string;
}

export default function ClassScheduleView({ classes, staff, emptyStateMessage }: Props) {
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const nurseryId = profile?.nursery_id;
  const printRef = useRef<HTMLDivElement>(null);
  const [selectedClassId, setSelectedClassId] = useState<string>(classes[0]?.id || "");
  const [addOpen, setAddOpen] = useState(false);
  const [editSlot, setEditSlot] = useState<any>(null);
  const [newSlot, setNewSlot] = useState<SlotForm>({ ...emptySlot });
  const [editForm, setEditForm] = useState<SlotForm>({ ...emptySlot });

  useEffect(() => {
    if (!classes.length) { setSelectedClassId(""); setAddOpen(false); return; }
    if (!classes.some((c) => c.id === selectedClassId)) setSelectedClassId(classes[0].id);
  }, [classes, selectedClassId]);

  const { data: schedules, isLoading } = useQuery({
    queryKey: ["class-schedules", selectedClassId],
    queryFn: async () => {
      if (!selectedClassId) return [];
      const { data } = await supabase
        .from("class_schedules")
        .select("*, staff(full_name)")
        .eq("class_id", selectedClassId)
        .order("day_of_week")
        .order("start_time");
      return data || [];
    },
    enabled: !!selectedClassId,
  });

  // Fetch assignments for the selected class
  const { data: classAssignments } = useQuery({
    queryKey: ["class-assignments-for-schedule", selectedClassId],
    queryFn: async () => {
      if (!selectedClassId) return [];
      const { data } = await supabase
        .from("assignments")
        .select("id, title, due_date")
        .eq("class_id", selectedClassId)
        .order("due_date", { ascending: false })
        .limit(50);
      return data || [];
    },
    enabled: !!selectedClassId,
  });

  const addMutation = useMutation({
    mutationFn: async () => {
      if (!nurseryId || !selectedClassId) throw new Error(emptyStateMessage || "لا يوجد فصل متاح");
      const { error } = await supabase.from("class_schedules").insert({
        class_id: selectedClassId, nursery_id: nurseryId,
        day_of_week: newSlot.day_of_week, start_time: newSlot.start_time, end_time: newSlot.end_time,
        subject: newSlot.subject, teacher_id: newSlot.teacher_id || null, room: newSlot.room || null,
        notes: newSlot.notes || null, assignment_id: newSlot.assignment_id || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["class-schedules", selectedClassId] });
      toast({ title: "تمت إضافة الحصة ✅" });
      setAddOpen(false);
      setNewSlot({ ...emptySlot });
    },
    onError: (e: any) => toast({ title: "خطأ", description: e.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (!editSlot) return;
      const { error } = await supabase.from("class_schedules").update({
        day_of_week: editForm.day_of_week, start_time: editForm.start_time, end_time: editForm.end_time,
        subject: editForm.subject, teacher_id: editForm.teacher_id || null, room: editForm.room || null,
        notes: editForm.notes || null, assignment_id: editForm.assignment_id || null,
      }).eq("id", editSlot.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["class-schedules", selectedClassId] });
      toast({ title: "تم تعديل الحصة ✅" });
      setEditSlot(null);
    },
    onError: (e: any) => toast({ title: "خطأ", description: e.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("class_schedules").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["class-schedules", selectedClassId] });
      toast({ title: "تم حذف الحصة" });
    },
  });

  const openEdit = (slot: any) => {
    setEditSlot(slot);
    setEditForm({
      day_of_week: slot.day_of_week,
      start_time: slot.start_time?.slice(0, 5) || "08:00",
      end_time: slot.end_time?.slice(0, 5) || "08:45",
      subject: slot.subject || "",
      teacher_id: slot.teacher_id || "",
      room: slot.room || "",
      notes: slot.notes || "",
      assignment_id: slot.assignment_id || "",
    });
  };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open("", "_blank");
    if (!win) return;
    const className = classes.find(c => c.id === selectedClassId)?.name || "";
    win.document.write(`<!DOCTYPE html><html dir="rtl"><head><meta charset="utf-8"><title>الجدول الأسبوعي - ${className}</title>
    <style>
      body { font-family: 'Segoe UI', Tahoma, sans-serif; padding: 20px; direction: rtl; }
      h2 { text-align: center; margin-bottom: 20px; }
      .day-title { font-weight: bold; margin: 16px 0 8px; font-size: 16px; border-bottom: 2px solid #333; padding-bottom: 4px; }
      .slot { display: flex; gap: 16px; padding: 8px 12px; border: 1px solid #ddd; border-radius: 6px; margin-bottom: 6px; }
      .slot-subject { font-weight: bold; min-width: 120px; }
      .slot-time { color: #666; min-width: 100px; }
      .slot-teacher { color: #888; }
      .slot-notes { color: #555; font-style: italic; }
      @media print { body { padding: 0; } }
    </style></head><body>
    <h2>الجدول الأسبوعي - ${className}</h2>${content.innerHTML}
    </body></html>`);
    win.document.close();
    win.print();
  };

  const grouped = (schedules || []).reduce((acc: Record<number, any[]>, s: any) => {
    (acc[s.day_of_week] = acc[s.day_of_week] || []).push(s);
    return acc;
  }, {});

  const assignments = classAssignments || [];

  const SlotFormFields = ({ form, setForm }: { form: SlotForm; setForm: (fn: (p: SlotForm) => SlotForm) => void }) => (
    <div className="space-y-3">
      <div>
        <Label>اليوم</Label>
        <Select value={String(form.day_of_week)} onValueChange={(v) => setForm(p => ({ ...p, day_of_week: Number(v) }))}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>{DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>من</Label><Input type="time" value={form.start_time} onChange={(e) => setForm(p => ({ ...p, start_time: e.target.value }))} /></div>
        <div><Label>إلى</Label><Input type="time" value={form.end_time} onChange={(e) => setForm(p => ({ ...p, end_time: e.target.value }))} /></div>
      </div>
      <div><Label>المادة *</Label><Input value={form.subject} onChange={(e) => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="لغة عربية" /></div>
      <div>
        <Label>المعلمة</Label>
        <Select value={form.teacher_id} onValueChange={(v) => setForm(p => ({ ...p, teacher_id: v }))}>
          <SelectTrigger><SelectValue placeholder="اختياري" /></SelectTrigger>
          <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>القاعة</Label><Input value={form.room} onChange={(e) => setForm(p => ({ ...p, room: e.target.value }))} placeholder="قاعة 1" /></div>
      <div>
        <Label className="flex items-center gap-1"><StickyNote className="w-3.5 h-3.5" /> ملاحظات</Label>
        <Textarea value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="مثال: تسليم واجب الرياضيات" rows={2} className="resize-none" />
      </div>
      <div>
        <Label className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> ربط واجب</Label>
        <Select value={form.assignment_id} onValueChange={(v) => setForm(p => ({ ...p, assignment_id: v === "none" ? "" : v }))}>
          <SelectTrigger><SelectValue placeholder="بدون واجب" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">بدون واجب</SelectItem>
            {assignments.map((a) => <SelectItem key={a.id} value={a.id}>{a.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calendar className="w-5 h-5 text-primary" />
          الجدول الأسبوعي
        </CardTitle>
        <div className="flex items-center gap-2">
          <Select value={selectedClassId} onValueChange={setSelectedClassId} disabled={!classes.length}>
            <SelectTrigger className="w-44"><SelectValue placeholder="اختر الفصل" /></SelectTrigger>
            <SelectContent>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>

          {selectedClassId && Object.keys(grouped).length > 0 && (
            <Button variant="outline" size="sm" className="gap-1" onClick={handlePrint}>
              <Printer className="w-4 h-4" /> طباعة
            </Button>
          )}

          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1" disabled={!selectedClassId}><Plus className="w-4 h-4" /> حصة</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" dir="rtl">
              <DialogHeader><DialogTitle>إضافة حصة جديدة</DialogTitle></DialogHeader>
              <SlotFormFields form={newSlot} setForm={setNewSlot as any} />
              <Button onClick={() => addMutation.mutate()} disabled={!newSlot.subject || addMutation.isPending} className="w-full mt-2">
                {addMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "إضافة"}
              </Button>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : !selectedClassId ? (
          <p className="text-center text-muted-foreground py-8">{emptyStateMessage || "اختر فصلاً لعرض الجدول"}</p>
        ) : Object.keys(grouped).length === 0 ? (
          <p className="text-center text-muted-foreground py-8">لا توجد حصص مضافة بعد</p>
        ) : (
          <>
            <div className="space-y-4" ref={printRef}>
              {DAYS.map((dayName, dayIdx) => {
                const slots = grouped[dayIdx];
                if (!slots?.length) return null;
                return (
                  <div key={dayIdx}>
                    <h4 className="font-semibold text-sm mb-2 text-muted-foreground day-title">{dayName}</h4>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {slots.map((slot: any) => {
                        const linkedAssignment = assignments.find(a => a.id === slot.assignment_id);
                        return (
                          <div key={slot.id} className={`p-3 rounded-lg border ${DAY_COLORS[dayIdx]} flex items-start justify-between gap-2 slot`}>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm slot-subject">{slot.subject}</p>
                              <p className="text-xs text-muted-foreground mt-0.5 slot-time">
                                {slot.start_time?.slice(0, 5)} - {slot.end_time?.slice(0, 5)}
                              </p>
                              {(slot as any).staff?.full_name && (
                                <p className="text-xs text-muted-foreground slot-teacher">{(slot as any).staff.full_name}</p>
                              )}
                              {slot.room && <p className="text-xs text-muted-foreground">📍 {slot.room}</p>}
                              {slot.notes && (
                                <p className="text-xs text-muted-foreground mt-1 italic slot-notes">📝 {slot.notes}</p>
                              )}
                              {linkedAssignment && (
                                <Badge variant="outline" className="mt-1 text-[10px] gap-1">
                                  <FileText className="w-3 h-3" /> {linkedAssignment.title}
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-col gap-1 print:hidden">
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" onClick={() => openEdit(slot)}>
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive/60 hover:text-destructive" onClick={() => deleteMutation.mutate(slot.id)}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={!!editSlot} onOpenChange={(o) => !o && setEditSlot(null)}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader><DialogTitle>تعديل الحصة</DialogTitle></DialogHeader>
          <SlotFormFields form={editForm} setForm={setEditForm as any} />
          <Button onClick={() => updateMutation.mutate()} disabled={!editForm.subject || updateMutation.isPending} className="w-full mt-2">
            {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ التعديلات"}
          </Button>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
